import {useLocation, useParams, useSearchParams} from '@github-ui/react-router'
import {useNavigate} from '@github-ui/use-navigate'

import {issuesIndexQueryResolver} from '../helpers/issues-index-query-resolver'

/**
 * Repo Issues#index query/navigation state: resolves the query from the URL and
 * navigates `/:owner/:repo/issues`. Shared by the search input and results list.
 */
export function useIssuesIndexResults() {
  const location = useLocation()
  const navigate = useNavigate()
  const {owner, repo} = useParams<{owner: string; repo: string}>()
  const [searchParams] = useSearchParams()

  const fetchedQuery = issuesIndexQueryResolver({searchParams})
  const currentPage = Number(searchParams.get('page') || 1)
  const pathname = owner && repo ? `/${owner}/${repo}/issues` : location.pathname

  function buildSearch(q: string, page = 1) {
    const nextSearchParams = new URLSearchParams()
    const normalizedQuery = q.trim()
    if (normalizedQuery) nextSearchParams.set('q', normalizedQuery)
    if (page > 1) nextSearchParams.set('page', String(page))

    for (const [key, value] of searchParams) {
      if (key !== 'q' && key !== 'page') nextSearchParams.append(key, value)
    }

    return nextSearchParams.toString()
  }

  function getQueryHref(q: string, page = 1) {
    const search = buildSearch(q, page)
    return `${pathname}${search ? `?${search}` : ''}`
  }

  function fetchResults({q, page = 1}: {q: string; page?: number}) {
    const search = buildSearch(q, page)
    navigate({pathname, search: search ? `?${search}` : ''})
    window.scrollTo({top: 0})
  }

  function getPageHref(page: number) {
    return getQueryHref(fetchedQuery, page)
  }

  return {currentPage, fetchedQuery, fetchResults, getPageHref, getQueryHref}
}
